<template>
    <AppContainer>
        <AppBreadcrumb :items="breadcrumbItems" />
        <el-row class="align-center">
            <h1>Api usages</h1>
        </el-row>
        <SearchArea
            label="api usages"
            :pagination-data="paginationData"
            :headers="headers"
            :query="query"
            :loading="isLoading"
            :action="false"
            @search="handleClickSearch"
            @size-change="handleClickSizeChange"
            @current-change="handleClickCurrentChange"
            @sort="handleClickSort"
        >
            <template #searchInputs>
                <el-row :gutter="10" class="app-search">
                    <el-col :sm="6">
                        <el-date-picker
                            v-model="searchQuery.date"
                            type="month"
                            format="YYYY-MM"
                            value-format="YYYY-MM"
                            placeholder="Date"
                            @change="handleClickSearch"
                        ></el-date-picker>
                    </el-col>
                    <el-col :sm="6">
                        <el-input
                            v-model="searchQuery.apiEndpoint"
                            clearable
                            placeholder="Api endpoint"
                            class="mr-4"
                            @change="handleClickSearch"
                        />
                    </el-col>
                    <el-col :sm="6">
                        <el-input
                            v-model="searchQuery.organizationName"
                            clearable
                            placeholder="Organization name"
                            class="mr-4"
                            @change="handleClickSearch"
                        />
                    </el-col>
                    <el-col :sm="6">
                        <el-input
                            v-model="searchQuery.licenseKey"
                            clearable
                            placeholder="License key"
                            class="mr-4"
                            @change="handleClickSearch"
                        />
                    </el-col>
                </el-row>
            </template>
        </SearchArea>
    </AppContainer>
</template>

<script lang="ts">
import SearchArea from '@/components/SearchArea.vue';
import {
    DEFAULT_QUERY,
    QueryPaginate,
    SortEvent,
} from '@/types/PaginationData';
import { defineComponent, reactive, computed, toRefs } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AppBreadcrumb from '@/components/AppBreadcrumb.vue';
import AppContainer from '@/components/AppContainer.vue';
import { useStore } from 'vuex';
import { SearchApiUsages } from '@/types/ApiUsages';

export default defineComponent({
    components: { AppContainer, AppBreadcrumb, SearchArea },
    setup() {
        const router = useRouter();
        const store = useStore();
        const data = reactive({
            breadcrumbItems: [
                {
                    text: 'Index',
                    to: '/',
                    exact: true,
                },
                { text: 'Api usages' },
            ],
            headers: [
                {
                    text: 'License ID',
                    value: 'licenseId',
                    selected: true,
                    sortable: true,
                },
                {
                    text: 'License key',
                    value: 'licenseKey',
                    selected: true,
                    sortable: false,
                },
                {
                    text: 'Organization Name',
                    value: 'organizationName',
                    selected: true,
                    sortable: true,
                },
                {
                    text: 'Api Endpoint',
                    value: 'apiEndpoint',
                    selected: true,
                    sortable: false,
                },
                {
                    text: 'Total Count',
                    value: 'totalCount',
                    selected: true,
                    sortable: true,
                },
                {
                    text: 'Date',
                    value: 'date',
                    sortable: true,
                    selected: true,
                },
            ],
            searchQuery: {
                date: '',
                organizationName: '',
                licenseKey: '',
                apiEndpoint: '',
            } as SearchApiUsages,
            paginationData: computed(() => {
                return store.getters['apiUsages/paginationData'];
            }),
            isLoading: computed(() => {
                return store.state.apiUsages.isLoading;
            }),
            query: computed(() => {
                return store.state.apiUsages.query;
            }),
        });

        const handleClickSearch = () => {
            store.dispatch('apiUsages/setSearch', data.searchQuery);
            router.replace({ query: store.state.apiUsages.query as any });
        };

        const handleClickSort = (event: SortEvent) => {
            store.dispatch('apiUsages/setSort', event);
        };

        const handleClickSizeChange = (limit: number) => {
            const event: QueryPaginate = {
                limit: limit,
                offset: 0,
            };
            store.dispatch('apiUsages/setPaginate', event);
            router.replace({ query: store.state.apiUsages.query as any });
        };

        const handleClickCurrentChange = (event: QueryPaginate) => {
            store.dispatch('apiUsages/setPaginate', event);
            router.replace({
                query: store.state.apiUsages.query as any,
            });
        };

        return {
            ...toRefs(data),
            handleClickSearch,
            handleClickSort,
            handleClickSizeChange,
            handleClickCurrentChange,
        };
    },
    created() {
        const router = useRouter();
        const store = useStore();
        store.dispatch('apiUsages/setSearch');
        router.replace({ query: store.state.apiUsages.query as any });
    },
});
</script>
